// biome-ignore-all lint/correctness/useHookAtTopLevel: false positive — Biome's component detection misses this generic row component, so it flags every (genuinely top-level) hook call.
/**
 * Timeline component for the Indigo design system.
 *
 * A virtualized timeline component that displays chronological information with advanced filtering.
 * Built with react-window for optimal performance with large datasets.
 *
 * Features:
 * - Virtualized rendering with dynamic row heights using react-window
 * - Multi-filter system: tags, search, type, and date range
 * - Client-side filtering and sorting (newest to oldest)
 * - Responsive layout with filter controls
 * - Single/multiple item selection support
 * - Expandable content with truncation
 * - Edit mode with external diff viewing control
 *
 * Architecture:
 * - useTimeline hook manages filtering, sorting, and selection state
 * - VirtualizedList handles efficient rendering of large datasets
 * - FilterBar provides consistent filter UI components
 * - TimelineItem renders individual timeline entries
 *
 * Diff Viewing in Edit Mode:
 * - Set `mode="edit"` to enable editing
 * - Populate `originalContent` field on each item when entering edit mode
 * - Use `showDiff` prop to toggle diff view for ALL items at once
 * - Inline diff highlights additions (green) and deletions (red with strikethrough)
 * - Update `originalContent` on save to reset the diff baseline
 * - Control diff viewing externally (e.g., with "Show Diffs" button in parent)
 *
 * @example
 * ```tsx
 * const [showDiff, setShowDiff] = useState(false);
 * const timelineData = [
 *   {
 *     id: "1",
 *     date: "2024-01-01",
 *     doctorName: "Dr. Smith",
 *     sourceTag: "AA",
 *     startPageNumber: 1,
 *     endPageNumber: 3,
 *     type: "surgery",
 *     typeLabel: "Surgery Report",
 *     tags: ["L3", "L4"],
 *     content: "Patient underwent surgical procedure...",
 *     originalContent: "Patient underwent surgical procedure..." // Set when entering edit mode
 *   }
 * ];
 *
 * <Timeline
 *   items={timelineData}
 *   typeConfig={medicalTypeConfig}
 *   mode="edit"
 *   showDiff={showDiff}
 *   onItemSelect={(items) => console.log('Selected:', items)}
 *   onItemChange={(itemId, changes) => {
 *     // Update the item with changes
 *     console.log('Item changed:', itemId, changes);
 *   }}
 * />
 * ```
 */
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { clsx } from "clsx";
import { atom, useStore } from "jotai";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ListImperativeAPI, RowComponentProps } from "react-window";
import { List, useDynamicRowHeight } from "react-window";
import { useCommandEffect } from "~/hooks/useCommandEffect";
import { useContainerBreakpoint } from "~/hooks/useContainerBreakpoint";
import { useIsSmallScreen } from "~/hooks/useIsSmallScreen";
import { DateISO } from "../../utils/DateISO";
import { Badge } from "../Badge/Badge";
import { Button } from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import { DropdownSelector } from "../DropdownSelector/DropdownSelector";
import { IconButton } from "../IconButton/IconButton";
import { Icon } from "../Icons/Icons";
import { MobileSheet } from "../MobileSheet/MobileSheet";
import { TextInput } from "../TextInput/TextInput";
import { TimelineItem } from "../TimelineItem/TimelineItem";
import {
  TimelineItemAction,
  TimelineItemData,
  TimelineItemTypeConfig,
} from "../TimelineItem/types";
import type { SummaryCitationAnchor } from "../TimelineItem/utils/citationMarkers";
import { Body } from "../Typography/Body";
import "./Timeline.css";
import { AppointmentFilterDropdown } from "./components/AppointmentFilterDropdown";
import { TimelineBulkActionsFooter } from "./components/TimelineBulkActionsFooter";
import { useTimeline } from "./hooks/useTimeline";
import { useTimelineKeyboard } from "./hooks/useTimelineKeyboard";
import { focusContentInItem } from "./utils/domUtils";
import { matchesFileFilter } from "./utils/filterAndSortTimelineItems";

const HACK_SCROLL_RETRY_DELAY_MS = 100;

/** Trailing debounce before a typed query reaches the filter pass. Searching
 * re-filters every entry (lowercasing each multi-KB content string) and then
 * re-scans the survivors to count matches, so an 800-entry MRR pays a
 * multi-megabyte sweep per keystroke — worst case on the FIRST character,
 * where almost nothing is filtered out. */
const SEARCH_DEBOUNCE_MS = 200;

/** Estimate for an unmeasured EXPANDED row — the median measured entry height
 * on a large MRR (n=680: p10 350, median 497, mean 571, p90 877). Median rather
 * than mean on purpose: it splits over- and under-estimates roughly evenly, so
 * corrections cancel as you scroll instead of accumulating in one direction. */
const EXPANDED_DEFAULT_ROW_HEIGHT = 500;

/** Estimate for an unmeasured COLLAPSED row — the measured median with the list
 * collapsed (n=66: p10 148, median 200, mean 185, p90 232, max 271). Collapsed
 * entries render the header only (TimelineItemContentV2 gates the body, video
 * button and footer on `isExpanded`), so they are both far shorter and far more
 * uniform than expanded ones: a 1.8x spread versus 4.5x. That tight spread is
 * why the estimate matters less here in the tail — but reusing the expanded
 * default would over-estimate by ~2.5x, the same class of bug as the original
 * 100-vs-500 under-estimate, just inverted. */
const COLLAPSED_DEFAULT_ROW_HEIGHT = 200;

/** Filter-row width below which the checkbox filters fold in behind the
 * filter toggle: narrower than this they no longer share a line with the
 * search box and type dropdown. */
const COMPACT_FILTERS_WIDTH = 640;

/**
 * Command to scroll to a specific item in the timeline
 */
export interface ScrollToTimelineItemCommand {
  action: "SCROLL_TO_ITEM";
  payload: {
    itemId: string;
    // Whether to select the item after scrolling to it
    // @default false
    selectItem?: boolean;
    // Whether to flash the item after scrolling to it (e.g. for a highlight effect)
    // Useful for indicating the selected item in edit mode, basically show highlight temporarily without selecting it
    // @default false
    flashItem?: boolean;
    // Whether to align the item to the start, center, end, or auto (default)
    // @default "auto"
    align?: "start" | "center" | "end" | "auto";
    // Whether to scroll to the item instantly or smoothly
    // @default "instant"
    scrollBehavior?: "instant" | "smooth" | "auto";
    // Whether to focus the content area (TipTap editor) after scrolling
    // Used when entering edit mode via "e" key to focus the editable content directly
    // @default false
    focusContentArea?: boolean;
  };
  timestamp: number;
}

/**
 * Atom for timeline scroll commands.
 * Export this atom to send commands from parent components.
 */
export const timelineCommandAtom = atom<ScrollToTimelineItemCommand | null>(
  null,
);

export interface TimelineFilterCommand {
  /**
   * When set, replaces the "Select file" filter with this list of pdf file
   * names. Other filters are left untouched. Useful for cross-tab deep
   * links (e.g. clicking a sub-rosa file in UploadTab jumps to MRR with
   * only that file's entries visible).
   *
   * NOTE: this matches by file name, not UploadedFile ID — same as the
   * existing in-timeline "Select file" dropdown. If a file is renamed
   * between dispatch and apply, the filter silently matches nothing.
   * Acceptable today because renames are rare and the dropdown has the
   * same limitation; migrate both call sites to IDs if that becomes a
   * real source of confusion.
   */
  selectedFiles?: string[];
  timestamp: number;
}

/**
 * Atom for imperatively overriding timeline filter state.
 * Set this atom to apply filters from outside the Timeline component.
 */
export const timelineFilterCommandAtom = atom<TimelineFilterCommand | null>(
  null,
);

/**
 * Filter state for the timeline
 */
export interface TimelineFilters {
  /**
   * Search query for content/title filtering
   */
  searchQuery: string;

  /**
   * Selected types to filter by
   */
  selectedTypes: string[];

  /**
   * Date range filter
   */
  dateRange: {
    startDate: DateISO | null;
    endDate: DateISO | null;
  };

  /**
   * Show only starred items
   */
  showStarredOnly: boolean;

  /**
   * Show only important items
   */
  showImportantOnly: boolean;

  /**
   * Selected PDF file names to filter by
   */
  selectedFiles: string[];
}

/**
 * Timeline component props
 */
export interface TimelineProps<T extends string = string> {
  /**
   * Array of timeline items to display
   */
  items: TimelineItemData<T>[];

  /**
   * Configuration mapping of type keys to their visual representation
   */
  typeConfig: Record<T, TimelineItemTypeConfig>;

  /**
   * Callback when an item is clicked
   * @param item - The item that was clicked
   */
  onItemClick?: (item: TimelineItemData<T>, selected: boolean) => void;

  /**
   * Callback when items are selected/deselected
   * @param selectedItems - Array of selected item IDs
   */
  onItemSelect?: (selectedItems: string[]) => void;

  /**
   * Initial filter state
   */
  initialFilters?: Partial<TimelineFilters>;

  /**
   * Whether to allow multiple item selection
   * @default true
   */
  allowMultipleSelection?: boolean;

  /**
   * Force expand all timeline items
   * @default false
   */
  expandAll?: boolean;

  /**
   * Mode for timeline items - display or edit
   * @default "display"
   */
  mode?: "display" | "edit";

  /**
   * Whether to show inline diff view in edit mode
   * When true, all items in edit mode will display inline diffs
   * @default false
   */
  showDiff?: boolean;

  /**
   * Whether we're currently syncing changes (edit mode only) and show loading spinner
   */
  isSyncing?: boolean;

  /**
   * ISO timestamp of the most recent edit to drafts/changes
   * Used to show "Last edited: X ago" in edit mode when different from lastPublishedAt
   */
  lastEditedAt?: string | null;

  /**
   * ISO timestamp of when the briefing was last published
   * Used to show "Last published: X ago" when no pending edits
   */
  lastPublishedAt?: string | null;

  /**
   * Count of drafts (locally and server-side)
   * Used to show accurate edit counts in the bulk actions footer
   */
  totalDraftsCount?: number;

  /**
   * Available tags for the tag selector in edit mode
   */
  availableTags?: string[];

  /**
   * Claimed body parts to show in "In the claim" filter row
   * Body parts not in this list will appear in "Other in MR" row
   */
  claimedBodyParts?: string[];

  /**
   * Callback when an item is changed in edit mode
   */
  onItemChange?: (
    itemId: string,
    changes: Partial<TimelineItemData<T>>,
  ) => void;

  /**
   * Callback when an action button is clicked in edit mode
   */
  onItemActionClick?: (itemId: string, action: TimelineItemAction) => void;

  /**
   * Callback when bulk discard changes is triggered (edit mode only)
   * @param itemIds - Array of selected item IDs to discard changes for
   */
  onBulkDiscardChanges?: (itemIds: string[]) => Promise<void>;

  /**
   * Callback when bulk delete is triggered (edit mode only)
   * @param itemIds - Array of selected item IDs to delete
   */
  onBulkDelete?: (itemIds: string[]) => Promise<void>;

  /**
   * Callback to compare the selected entries side by side (edit mode only).
   * @param itemIds - Array of selected item IDs
   */
  onCompare?: (itemIds: string[]) => void;

  /**
   * Callback when add entry is triggered (edit mode only)
   * Triggered by Ctrl+Cmd+N (Mac) / Ctrl+Alt+N (Windows) keyboard shortcut
   */
  onAddEntry?: () => void;

  /**
   * Callback when user requests edit mode from display mode (pressing "e" on focused item)
   * @param focusedItemId - The ID of the item that was focused when edit mode was requested
   */
  onRequestEditMode?: (focusedItemId: string) => void;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Test ID for the timeline container
   */
  "data-testid"?: string;

  /**
   * Sort order for timeline items. Defaults to "desc" (newest to oldest).
   */
  sortOrder?: "asc" | "desc";

  /**
   * Priority map for types when sorting items with equal dates.
   * Lower number = higher priority (appears first).
   * Types not in map default to 999 (lowest priority).
   */
  typePriority?: Partial<Record<T, number>>;

  /** Sort suggest-edit → suggest-delete → other entries (edit mode only). */
  groupBySuggestion?: boolean;

  /** Toggles `groupBySuggestion`; the checkbox renders only when provided. */
  onGroupBySuggestionChange?: (value: boolean) => void;

  /**
   * Callback when a segment suggestion is accepted or rejected
   * @param suggestionId - The ID of the suggestion
   * @param action - 'accept' or 'reject'
   */
  onSuggestionAction?: (
    suggestionId: string,
    action: "accept" | "reject",
  ) => void;

  /**
   * Callback when keyboard/click focus changes to a new item.
   * Used by parent to track which segments the user has navigated to.
   * @param itemId - The ID of the newly focused item, or null if focus cleared
   */
  onFocusedItemChange?: (itemId: string | null) => void;

  /**
   * Render function for extra content within each timeline item.
   * Called with the item ID. Content is rendered between header and body in V2 display mode.
   */
  renderValidationIssues?: (itemId: string) => React.ReactNode;

  /**
   * Whether citations are available to this user. Passed in rather than
   * read here: the user's role is an app-level concern and this is a
   * design-system component, so it takes the answer as a prop.
   */
  citationsEnabled?: boolean;

  /**
   * Source citations per entry, keyed by `logicalSegmentID`. Gated upstream by
   * the user's role; absent when citations are unavailable to them.
   */
  citationsByItemId?: ReadonlyMap<string, readonly SummaryCitationAnchor[]>;

  /** Jump the document viewer to a citation's source block. */
  onOpenCitation?: (citationRowId: string) => void;

  /** Soft-delete one citation link. */
  onRemoveCitation?: (citationRowId: string) => void;

  /**
   * Set of item IDs whose rerun is currently in flight. Items in this set
   * are rendered grayed-out and their rerun button is hidden.
   */
  rerunningItemIds?: Set<string>;

  /**
   * Set of item IDs that just finished a successful rerun. Used to render a
   * transient "Resummarized" tag on those items.
   */
  recentlyRerunItemIds?: Set<string>;

  /**
   * Set of item IDs whose most recent rerun came back FAILED from the
   * lambda. Used to render a "Summarization failed" tag on those items.
   */
  failedRerunItemIds?: Set<string>;

  /**
   * Whether the rerun action button should be rendered on timeline items.
   * Callers gate this behind a feature flag. Defaults to true.
   */
  showRerunAction?: boolean;

  /**
   * Whether the "File a ticket" button should be rendered on timeline
   * items. Callers gate this on user role (e.g. hidden for doctors).
   * Defaults to true.
   */
  showFileTicketAction?: boolean;

  /**
   * Tooltip text for the rerun button on each timeline item. Threaded
   * through to TimelineItem so callers can swap the wording by feature
   * flag (e.g. "Split and resummarization" when the split flow is active).
   */
  rerunTooltip?: string;

  /**
   * Called when SCROLL_TO_ITEM targets an item that exists in `items` but is
   * hidden by the active "Select file" filter (selectedFiles). Lets the
   * parent surface a toast/banner explaining why the scroll was a no-op.
   */
  onScrollItemHiddenByFileFilter?: (item: TimelineItemData<T>) => void;

  /**
   * Per-record-type action visibility overrides. Threaded down to each
   * TimelineItem so callers can hide specific actions for items whose
   * `type` matches a key in this map (e.g. hide OCR text + rerun for
   * sub-rosa video entries).
   */
  actionVisibilityByRecordType?: Partial<
    Record<T, Partial<Record<TimelineItemAction, boolean>>>
  >;

  /**
   * Callback when the inline "View video" button is clicked on a timeline
   * item (display mode only). Threaded down to TimelineItem; the button is
   * only rendered for sub-rosa video entries (gated inside TimelineItem).
   */
  onViewVideoClick?: (itemId: string) => void;

  /**
   * Map of UploadedFile ID → source video duration (seconds). Used by the
   * timeline-item edit mode to validate that sub-rosa start/end times
   * don't exceed the source clip. Keyed on ID (not file name) so renames
   * don't break the lookup. Missing entries skip the check.
   */
  videoDurationByUploadedFileID?: Record<string, number>;

  /**
   * Options for the related-appointments filter dropdown. When provided,
   * renders a dropdown in the filter bar allowing users to select which
   * appointments' entries to display. Only shown for chain appointments
   * (re-eval, supplemental, deposition).
   */
  appointmentFilterOptions?: {
    id: string;
    label: string;
    isCurrent: boolean;
    disabled?: boolean;
  }[];

  /**
   * Currently selected appointment IDs in the related-appointments filter.
   */
  selectedAppointmentIds?: string[];

  /**
   * Callback when the selected appointments change.
   */
  onAppointmentFilterChange?: (ids: string[]) => void;

  /**
   * Whether briefings for non-current appointments are currently being
   * fetched. Drives a spinner on the appointment filter trigger.
   */
  appointmentFilterLoading?: boolean;

  /**
   * Whether the extra filters (file, date range) start expanded.
   * @default false
   */
  defaultFiltersExpanded?: boolean;

  /**
   * Custom content rendered in the body when no items are visible. Defaults
   * to the generic "no items match the current filters" message. Override to
   * give context-specific guidance — e.g. a chain appointment whose own
   * current appointment has no records, where the filter above is the only
   * affordance.
   */
  emptyStateContent?: ReactNode;
}

/**
 * Timeline row props for react-window
 */
interface TimelineRowProps<T extends string = string> {
  items: TimelineItemData<T>[];
  typeConfig: Record<T, TimelineItemTypeConfig>;
  selectedItems: Set<string>;
  expandedItems: Set<string>;
  onItemClick: (item: TimelineItemData<T>, shiftKey?: boolean) => void;
  onToggleCollapse: (itemId: string) => void;
  searchQuery: string;
  mode?: "display" | "edit";
  showDiff?: boolean;
  availableTags?: string[];
  onItemChange?: (
    itemId: string,
    changes: Partial<TimelineItemData<T>>,
  ) => void;
  onItemActionClick?: (itemId: string, action: TimelineItemAction) => void;
  currentMatchInfo:
    | {
        item: TimelineItemData<T>;
        itemIndex: number;
        matchIndex: number;
        totalMatchesInItem: number;
      }
    | undefined;
  unseenItemIndex: number | null;
  focusedItemId: string | null;
  onSuggestionAction?: (
    suggestionId: string,
    action: "accept" | "reject",
  ) => void;
  onNavigateToSegment?: (segmentId: string) => void;
  renderValidationIssues?: (itemId: string) => React.ReactNode;

  /**
   * Source citations per entry, keyed by `logicalSegmentID`. Gated upstream by
   * the user's role; absent when citations are unavailable to them.
   */
  citationsByItemId?: ReadonlyMap<string, readonly SummaryCitationAnchor[]>;

  /** Jump the document viewer to a citation's source block. */
  onOpenCitation?: (citationRowId: string) => void;

  /** Soft-delete one citation link. */
  onRemoveCitation?: (citationRowId: string) => void;
  rerunningItemIds?: Set<string>;
  recentlyRerunItemIds?: Set<string>;
  failedRerunItemIds?: Set<string>;
  showRerunAction?: boolean;
  showFileTicketAction?: boolean;
  rerunTooltip?: string;
  actionVisibilityByRecordType?: Partial<
    Record<T, Partial<Record<TimelineItemAction, boolean>>>
  >;
  onViewVideoClick?: (itemId: string) => void;
  videoDurationByUploadedFileID?: Record<string, number>;
}

/**
 * Clickable tag component for filters
 */
const ClickableTag: React.FC<{
  label: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ label, isSelected, onClick }) => {
  return (
    <button
      className={`indigo-timeline__filter-tag ${isSelected ? "indigo-timeline__filter-tag--selected" : ""}`}
      onClick={onClick}
      type="button"
      aria-pressed={isSelected}
    >
      {label}
    </button>
  );
};

/**
 * Individual row component for react-window virtualization
 */
const TimelineRow = function <T extends string>({
  index,
  style,
  ...props
}: RowComponentProps<TimelineRowProps<T>>) {
  const {
    items,
    typeConfig,
    selectedItems,
    expandedItems,
    onItemClick,
    onToggleCollapse,
    searchQuery,
    mode = "display",
    showDiff = false,
    availableTags,
    onItemChange,
    onItemActionClick,
    currentMatchInfo,
    unseenItemIndex,
    focusedItemId,
    onSuggestionAction,
    onNavigateToSegment,
    renderValidationIssues,
    citationsByItemId,
    onOpenCitation,
    onRemoveCitation,
    rerunningItemIds,
    recentlyRerunItemIds,
    failedRerunItemIds,
    showRerunAction = true,
    showFileTicketAction = true,
    rerunTooltip,
    actionVisibilityByRecordType,
    onViewVideoClick,
    videoDurationByUploadedFileID,
  } = props;
  const item = items[index];

  if (!item) return null;

  // Calculate item-specific values
  const isSelected = selectedItems.has(item.id);
  const isExpanded = expandedItems.has(item.id);
  const isLastItem = index === items.length - 1;
  const isUnseen = unseenItemIndex === index;
  const isRerunning = rerunningItemIds?.has(item.id) ?? false;
  const isRecentlyRerun = recentlyRerunItemIds?.has(item.id) ?? false;
  const isRerunFailed = failedRerunItemIds?.has(item.id) ?? false;

  // Determine which match in this item is active
  const activeMatchIndex =
    currentMatchInfo && currentMatchInfo.item.id === item.id
      ? currentMatchInfo.matchIndex
      : -1;

  // Stable onChange handler
  // Optimized: captures item.id in closure instead of depending on whole item object
  const handleChange = useCallback(
    (changes: Partial<TimelineItemData<T>>) => {
      onItemChange?.(item.id, changes);
    },
    [onItemChange, item.id],
  );

  // Stable action handler
  // Optimized: captures item.id in closure instead of depending on whole item object
  const handleActionClick = useCallback(
    (action: TimelineItemAction) => {
      onItemActionClick?.(item.id, action);
    },
    [onItemActionClick, item.id],
  );

  // Stable toggle collapse handler
  // Optimized: captures item.id in closure instead of depending on whole item object
  const handleToggleCollapse = useCallback(() => {
    onToggleCollapse(item.id);
  }, [onToggleCollapse, item.id]);

  // Stable click handler. This one has to depend on the whole `item` (unlike
  // its siblings above) because onItemClick takes the item, not the id — so it
  // is only as stable as `item` itself. An inline arrow here silently defeated
  // TimelineItem's React.memo entirely: react-window rebuilds its offset cache
  // on every row measurement, which re-renders every mounted row, which with a
  // broken memo re-parsed every visible entry's markdown.
  const handleClick = useCallback(
    (shiftKey?: boolean) => {
      onItemClick(item, shiftKey);
    },
    [onItemClick, item],
  );

  // Apply unseen class to the row wrapper.
  const rowClassName = `indigo-timeline-row ${isUnseen ? "js-unseen-row" : ""}`;

  return (
    <div style={style} className={rowClassName} data-item-index={index}>
      <TimelineItem
        key={item.id}
        item={item}
        typeConfig={typeConfig}
        selected={isSelected}
        expanded={isExpanded}
        showConnector={!isLastItem}
        onClick={handleClick}
        onToggleCollapse={handleToggleCollapse}
        searchQuery={searchQuery}
        activeMatchIndex={activeMatchIndex}
        mode={mode}
        showDiff={showDiff}
        onChange={handleChange}
        availableTags={availableTags}
        onActionClick={handleActionClick}
        className={isUnseen ? "js-unseen" : undefined}
        isKeyboardFocused={focusedItemId === item.id}
        rowNumber={index + 1}
        onSuggestionAction={onSuggestionAction}
        onNavigateToSegment={onNavigateToSegment}
        renderValidationIssues={renderValidationIssues}
        citations={citationsByItemId?.get(item.id)}
        onOpenCitation={onOpenCitation}
        onRemoveCitation={onRemoveCitation}
        isRerunning={isRerunning}
        isRecentlyRerun={isRecentlyRerun}
        isRerunFailed={isRerunFailed}
        showRerunAction={showRerunAction}
        showFileTicketAction={showFileTicketAction}
        rerunTooltip={rerunTooltip}
        actionVisibilityByRecordType={actionVisibilityByRecordType}
        onViewVideoClick={onViewVideoClick}
        maxDurationSec={
          item.uploadedFileID
            ? (videoDurationByUploadedFileID?.[item.uploadedFileID] ?? null)
            : null
        }
      />
    </div>
  );
} as <T extends string>(
  props: RowComponentProps<TimelineRowProps<T>>,
) => React.ReactElement;

/**
 * Timeline component
 *
 * Displays a virtualized timeline with advanced filtering capabilities.
 * Handles large datasets efficiently using react-window.
 */
export const Timeline = <T extends string = string>({
  items,
  typeConfig,
  onItemSelect,
  onItemClick,
  initialFilters,
  allowMultipleSelection = true,
  expandAll = false,
  mode = "display",
  showDiff = false,
  isSyncing = false,
  lastEditedAt,
  lastPublishedAt,
  totalDraftsCount = 0,
  availableTags: providedAvailableTags,
  claimedBodyParts = [],
  onItemChange,
  onItemActionClick,
  onBulkDiscardChanges,
  onBulkDelete,
  onCompare,
  onAddEntry,
  onRequestEditMode,
  className = "",
  sortOrder = "desc",
  typePriority,
  groupBySuggestion = false,
  onGroupBySuggestionChange,
  "data-testid": dataTestId,
  onSuggestionAction,
  onFocusedItemChange,
  renderValidationIssues,
  citationsEnabled = false,
  citationsByItemId,
  onOpenCitation,
  onRemoveCitation,
  rerunningItemIds,
  recentlyRerunItemIds,
  failedRerunItemIds,
  showRerunAction = true,
  showFileTicketAction = true,
  rerunTooltip,
  onScrollItemHiddenByFileFilter,
  actionVisibilityByRecordType,
  onViewVideoClick,
  videoDurationByUploadedFileID,
  appointmentFilterOptions,
  selectedAppointmentIds,
  onAppointmentFilterChange,
  appointmentFilterLoading = false,
  defaultFiltersExpanded = false,
  emptyStateContent,
}: TimelineProps<T>) => {
  // Reveals the `[1][2]` source markers. Off by default: the markers are a
  // verification aid, so the default read of an entry stays uninterrupted
  // prose. Their data is fetched either way, so toggling is instant. A display
  // concern only, so it lives here rather than in `filters` (which decides
  // which entries are shown at all).
  const [showCitations, setShowCitations] = useState(false);

  // Read the filter command atom synchronously at render time (no subscription).
  // This seeds initialFilters for useTimeline's useState on the very first render,
  // which handles the LazyTab race: when tabSwitcher activates RecordReviewTab for
  // the first time, Timeline mounts AFTER the atom is set, so useCommandEffect's
  // effect may fire too late. useStore().get() reads the value during render itself.
  const store = useStore();
  const filterCommandAtMount = store.get(timelineFilterCommandAtom);
  const mergedInitialFilters: typeof initialFilters =
    filterCommandAtMount?.selectedFiles
      ? { ...initialFilters, selectedFiles: filterCommandAtMount.selectedFiles }
      : initialFilters;

  // Use custom hook for timeline state management
  const {
    filteredItems,
    selectedItems,
    expandedItems,
    filters,
    claimedTags,
    otherTags,
    availableTags,
    availableTypes,
    availableFiles,
    handleItemClick,
    handleToggleCollapse,
    handleSearchChange,
    handleTypeFilter,
    handleDateRangeChange,
    handleStarredFilterChange,
    handleImportantFilterChange,
    handleFileFilter,
    matchCount,
    currentMatchIndex,
    currentMatchInfo,
    goToNextMatch,
    goToPreviousMatch,
    clearSelections,
    collapseItem,
    selectItem,
    focusedItemId,
    focusedItemIndex,
    setFocusedItemId,
  } = useTimeline({
    items,
    typeConfig,
    onItemSelect,
    onItemClick,
    initialFilters: mergedInitialFilters,
    allowMultipleSelection,
    expandAll,
    mode,
    sortOrder,
    claimedBodyParts,
    typePriority,
    groupBySuggestion,
  });

  // The search box is controlled locally so typing stays responsive, while the
  // expensive filter pass runs on a trailing debounce. It cannot simply mirror
  // `filters.searchQuery`: the body-part tag buttons write that directly and
  // must apply immediately. (The filter command atom does NOT — it only carries
  // `selectedFiles`.)
  const [searchDraft, setSearchDraft] = useState(filters.searchQuery);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  // What we last pushed downstream. Lets the sync effect below distinguish
  // "our own debounce landed" (ignore) from "something else set the query"
  // (adopt) — without it, a keystroke arriving between the timer firing and
  // the effect running would be clobbered back to the older value.
  const lastPushedQueryRef = useRef(filters.searchQuery);

  useEffect(() => {
    if (filters.searchQuery !== lastPushedQueryRef.current) {
      // Someone else set the query (today: only the tag buttons). Drop any
      // pending keystroke: otherwise it lands ~200ms later and overwrites what
      // they just chose, leaving the box showing one thing while the list is
      // filtered by another.
      //
      // INVARIANT for future writers: this comparison only detects an external
      // write that CHANGES the value. The tag buttons are toggle-based, so they
      // always do. A writer that sets the query idempotently (a deep link, a
      // "jump to finding" affordance) could set the same string we last pushed
      // while a different keystroke is pending — the check would not fire, the
      // stale timer would survive, and it would overwrite that write. Route any
      // such writer through here, or compare against the pending value too.
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      lastPushedQueryRef.current = filters.searchQuery;
      setSearchDraft(filters.searchQuery);
    }
  }, [filters.searchQuery]);

  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchDraft(value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        searchTimerRef.current = null;
        lastPushedQueryRef.current = value;
        handleSearchChange(value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [handleSearchChange],
  );

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    [],
  );

  // Local state for showing/hiding "Other in MR" tags
  const [showOtherTags, setShowOtherTags] = useState(false);

  // Local state for showing/hiding the extra ("expanded") filters (file, date range)
  const [showAllFilters, setShowAllFilters] = useState(defaultFiltersExpanded);

  // The filter row adapts to the card's own width, not the viewport's — the
  // Record review card sits in a panel the user resizes independently. Below
  // this width the checkbox filters can't share a line with search + type, so
  // they fold in behind the filter toggle.
  const filtersRowRef = useRef<HTMLDivElement>(null);
  const compactFilters = useContainerBreakpoint(
    filtersRowRef,
    COMPACT_FILTERS_WIDTH,
  );

  // Below `md` the filter bar is a different thing altogether: the search box
  // and a "Filters" button on one line, everything else in a sheet behind
  // it. Stacked in a column, the eight controls plus the body-part rows took
  // the whole first screen and left the list ~100px to scroll in. Viewport,
  // not container: a bottom sheet is a phone affordance, and on a desk a
  // narrow panel already has `compactFilters` for the same DOM.
  const isNarrowViewport = useIsSmallScreen("md");
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

  // State for tracking unseen item (for flash effect)
  const [unseenItemIndex, setUnseenItemIndex] = useState<number | null>(null);
  const unseenTimerRef = useRef<NodeJS.Timeout | null>(null);

  // State for tracking first visible row (for position indicator in footer)
  const [firstVisibleRowIndex, setFirstVisibleRowIndex] = useState(0);

  // Ref to container for scrolling
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<ListImperativeAPI | null>(null);

  // Handle scroll commands via atom using useCommandEffect
  const handleScrollCommand = useCallback(
    (command: ScrollToTimelineItemCommand) => {
      const itemIndex = filteredItems.findIndex(
        (item) => item.id === command.payload.itemId,
      );
      if (itemIndex !== -1 && listRef.current) {
        const scrollArgs = {
          index: itemIndex,
          align: command.payload.align || "auto",
          behavior: command.payload.scrollBehavior || "instant",
        };
        listRef.current.scrollToRow(scrollArgs);
        // HACK: `react-window` has a scroll issue in dynamic item height mode that cause "smooth" scroll mode to not work properly.
        // The workaround described by this issue is to call scroll API again after a small delay.
        // https://github.com/bvaughn/react-window/issues/883
        setTimeout(() => {
          listRef?.current?.scrollToRow(scrollArgs);
        }, HACK_SCROLL_RETRY_DELAY_MS);
        if (command.payload.selectItem) {
          selectItem(command.payload.itemId);
          setFocusedItemId(command.payload.itemId);
          // Setting focusedItemId only paints the focus ring — it does NOT
          // move DOM focus. In display mode arrow-key nav reads keydown off
          // the scroll container, so a deep-link / validation-sidebar jump
          // that selects an item would show a focused-looking row that the
          // arrows can't move. Pull DOM focus onto the container so the
          // selected item is immediately keyboard-navigable.
          containerRef.current?.focus({ preventScroll: true });
        }
        if (command.payload.flashItem) {
          // Mark this item as unseen - will be handled by onRowsRendered
          setUnseenItemIndex(itemIndex);
        }
        if (command.payload.focusContentArea) {
          // Focus the content area - uses MutationObserver to wait for element to appear
          void focusContentInItem(
            listRef.current?.element ?? document.body,
            command.payload.itemId,
          );
        }
      } else if (itemIndex === -1 && filters.selectedFiles.length > 0) {
        const hiddenItem = items.find(
          (item) => item.id === command.payload.itemId,
        );
        if (
          hiddenItem &&
          !matchesFileFilter(hiddenItem, filters.selectedFiles)
        ) {
          onScrollItemHiddenByFileFilter?.(hiddenItem);
        }
      }
    },
    [
      filteredItems,
      items,
      filters.selectedFiles,
      selectItem,
      setFocusedItemId,
      onScrollItemHiddenByFileFilter,
    ],
  );

  useCommandEffect(timelineCommandAtom, handleScrollCommand);

  const handleFilterCommand = useCallback(
    (command: TimelineFilterCommand) => {
      if (command.selectedFiles) {
        handleFileFilter(command.selectedFiles);
      }
    },
    [handleFileFilter],
  );

  useCommandEffect(timelineFilterCommandAtom, handleFilterCommand);

  // Internal handler for navigating to a related segment (e.g., from duplicate suggestion "View original")
  const handleNavigateToSegment = useCallback(
    (segmentId: string) => {
      handleScrollCommand({
        action: "SCROLL_TO_ITEM",
        payload: {
          itemId: segmentId,
          flashItem: true,
          align: "start",
          // Measuring newly mounted editors interrupts smooth scrolling.
          scrollBehavior: "instant",
        },
        timestamp: Date.now(),
      });
    },
    [handleScrollCommand],
  );

  // Handle when rows are rendered - track first visible row and unseen item timer
  const handleRowsRendered = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      // When last item is visible, snap to it so indicator shows "N / N" at the bottom
      const isAtBottom = stopIndex >= filteredItems.length - 1;
      setFirstVisibleRowIndex(isAtBottom ? stopIndex : startIndex);

      if (
        unseenItemIndex !== null &&
        unseenItemIndex >= startIndex &&
        unseenItemIndex <= stopIndex
      ) {
        // Clear any existing timer
        if (unseenTimerRef.current) {
          clearTimeout(unseenTimerRef.current);
        }

        // Start new timer to remove unseen state after 5 seconds
        unseenTimerRef.current = setTimeout(() => {
          setUnseenItemIndex(null);
          unseenTimerRef.current = null;
        }, 5000);
      }
    },
    [unseenItemIndex, filteredItems.length],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (unseenTimerRef.current) {
        clearTimeout(unseenTimerRef.current);
      }
    };
  }, []);

  // Notify parent when focused item changes.
  // Use a ref for the callback so the effect only re-runs when focusedItemId
  // actually changes (the callback recreates on every pendingSuggestions update
  // due to markSegmentSeen → optimistic cache update chain).
  const onFocusedItemChangeRef = useRef(onFocusedItemChange);
  onFocusedItemChangeRef.current = onFocusedItemChange;
  useEffect(() => {
    onFocusedItemChangeRef.current?.(focusedItemId);
  }, [focusedItemId]);

  // Display items are just the filtered items (no adding item logic)
  const displayItems = filteredItems;

  // In display mode, clicking an entry opens the PDF preview in the side
  // panel; once the user moves into that pane the timeline loses DOM focus
  // and arrow-key nav goes dead (display mode, unlike edit mode, never
  // reclaims focus on its own). Pull focus back onto the scroll container
  // after the click so keyboard navigation stays available.
  const handleRowClick = useCallback(
    (item: TimelineItemData<T>, shiftKey?: boolean) => {
      handleItemClick(item, shiftKey);
      if (mode === "display") {
        containerRef.current?.focus({ preventScroll: true });
      }
    },
    [handleItemClick, mode],
  );

  // Keyboard navigation and shortcuts
  const { handleKeyDown, handleFocusIn } = useTimelineKeyboard({
    mode,
    displayItems,
    focusedItemId,
    focusedItemIndex,
    setFocusedItemId,
    handleItemClick,
    collapseItem,
    onItemActionClick,
    onAddEntry,
    onRequestEditMode,
    listRef,
    containerRef,
  });

  // Scroll to current match when it changes
  // Depend on primitive values (not object reference) to avoid re-triggering
  // on every render when searchMatches array is recalculated
  const currentMatchItemIndex = currentMatchInfo?.itemIndex;
  const currentMatchMatchIndex = currentMatchInfo?.matchIndex;
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    if (containerRef.current && currentMatchItemIndex != null) {
      // Find the item row element
      const rowElement = containerRef.current.querySelector(
        `[data-item-index="${currentMatchItemIndex}"]`,
      );
      if (rowElement) {
        // Try to scroll to the active <mark> element for precise positioning
        const activeMark = rowElement.querySelector(
          "mark.indigo-timeline-item__mark--active",
        );
        if (activeMark) {
          activeMark.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          // Fallback to scrolling to the item row
          rowElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  }, [currentMatchItemIndex, currentMatchMatchIndex]);

  // Row props for react-window
  const rowProps = useMemo<TimelineRowProps<T>>(
    () => ({
      items: displayItems,
      typeConfig,
      selectedItems,
      expandedItems,
      onItemClick: handleRowClick,
      onToggleCollapse: handleToggleCollapse,
      searchQuery: filters.searchQuery,
      mode,
      showDiff,
      availableTags: providedAvailableTags || availableTags,
      onItemChange,
      onItemActionClick,
      currentMatchInfo,
      unseenItemIndex,
      focusedItemId,
      onSuggestionAction,
      onNavigateToSegment: handleNavigateToSegment,
      renderValidationIssues,
      citationsByItemId: showCitations ? citationsByItemId : undefined,
      onOpenCitation,
      onRemoveCitation,
      rerunningItemIds,
      recentlyRerunItemIds,
      failedRerunItemIds,
      showRerunAction,
      showFileTicketAction,
      rerunTooltip,
      actionVisibilityByRecordType,
      onViewVideoClick,
      videoDurationByUploadedFileID,
    }),
    [
      displayItems,
      typeConfig,
      expandedItems,
      handleRowClick,
      handleToggleCollapse,
      selectedItems,
      filters.searchQuery,
      mode,
      showDiff,
      providedAvailableTags,
      availableTags,
      onItemChange,
      onItemActionClick,
      currentMatchInfo,
      unseenItemIndex,
      focusedItemId,
      onSuggestionAction,
      handleNavigateToSegment,
      renderValidationIssues,
      citationsByItemId,
      showCitations,
      onOpenCitation,
      onRemoveCitation,
      rerunningItemIds,
      recentlyRerunItemIds,
      failedRerunItemIds,
      showRerunAction,
      showFileTicketAction,
      rerunTooltip,
      actionVisibilityByRecordType,
      onViewVideoClick,
      videoDurationByUploadedFileID,
    ],
  );

  // Estimate for rows react-window hasn't measured yet. Fast scrolling skips
  // whole ranges of them (scrollbar drags, flings, React coalescing scroll
  // events), so those rows keep this value until they actually mount — and when
  // one above the viewport finally measures, every row below it shifts by the
  // error. 500 is the median measured entry height on a large MRR (n=680:
  // p10 350, median 497, mean 571, p90 877). Median rather than mean on
  // purpose: it splits over- and under-estimates roughly evenly, so corrections
  // cancel as you scroll instead of accumulating in one direction.
  // Collapse-all changes EVERY row's height at once, which is precisely what
  // `key` is for: it discards the whole measurement cache when every entry in
  // it has become wrong simultaneously. (Not the same situation as heights
  // going stale after a *filter*, where only unmounted rows are wrong and
  // mounted ones self-correct — resetting there would throw away good
  // measurements to fix a few bad ones.)
  //
  // Keyed on the `expandAll` prop rather than on `expandedItems`: expanding one
  // row via its hover button changes only that row, and the ResizeObserver
  // picks it up on its own. Only the bulk toggle invalidates everything.
  const rowHeight = useDynamicRowHeight({
    defaultRowHeight: expandAll
      ? EXPANDED_DEFAULT_ROW_HEIGHT
      : COLLAPSED_DEFAULT_ROW_HEIGHT,
    key: expandAll ? "expanded" : "collapsed",
  });

  /** Rendered either inline in the filter row or, on a narrow card, inside the
   * expandable group next to File / Date range. */
  const checkboxFilters = (
    <>
      {/* Starred Filter */}
      <div className="indigo-timeline__filter-group indigo-timeline__checkbox-filter">
        <Checkbox
          checked={filters.showStarredOnly}
          onChange={(e: CheckboxChangeEvent) =>
            handleStarredFilterChange(e.target.checked)
          }
        >
          <Body size="sm" className="font-medium">
            Favorited
          </Body>
        </Checkbox>
      </div>

      {/* Important Filter */}
      <div className="indigo-timeline__filter-group indigo-timeline__checkbox-filter">
        <Checkbox
          checked={filters.showImportantOnly}
          onChange={(e: CheckboxChangeEvent) =>
            handleImportantFilterChange(e.target.checked)
          }
        >
          <Body size="sm" className="font-medium">
            Important
          </Body>
        </Checkbox>
      </div>

      {/* Group-by-suggestion sort — edit mode only */}
      {mode === "edit" && onGroupBySuggestionChange && (
        <div className="indigo-timeline__filter-group indigo-timeline__checkbox-filter">
          <Checkbox
            checked={groupBySuggestion}
            onChange={(e: CheckboxChangeEvent) =>
              onGroupBySuggestionChange(e.target.checked)
            }
          >
            <Body size="sm" className="font-medium">
              Group suggestions
            </Body>
          </Checkbox>
        </div>
      )}
    </>
  );

  // ── Filter controls ──────────────────────────────────────────────────────
  // Defined once and placed by layout: in the filter row on a desk, in the
  // sheet on a phone. One definition is what guarantees the two layouts
  // filter the same way.

  const searchGroup = (
    <div className="indigo-timeline__filter-group indigo-timeline__search-group">
      <TextInput
        value={searchDraft}
        onChange={handleSearchInput}
        placeholder="Search for keyword"
        variant="outline"
        prefix={<Icon name="search" sizeX={12} sizeY={12} />}
      />
      {/* Search match navigation */}
      {matchCount > 0 && (
        <div className="indigo-timeline__search-navigation">
          <span className="indigo-timeline__search-counter">
            {currentMatchIndex + 1}/{matchCount}
          </span>
          <button
            className="indigo-timeline__search-nav-button"
            onClick={goToPreviousMatch}
            aria-label="Previous match"
            type="button"
          >
            <Icon name="arrow-up" sizeX={12} sizeY={12} />
          </button>
          <button
            className="indigo-timeline__search-nav-button"
            onClick={goToNextMatch}
            aria-label="Next match"
            type="button"
          >
            <span
              style={{
                display: "inline-block",
                transform: "rotate(180deg)",
              }}
            >
              <Icon name="arrow-up" sizeX={12} sizeY={12} />
            </span>
          </button>
        </div>
      )}
    </div>
  );

  /** Chain appointments only. */
  const appointmentFilter =
    appointmentFilterOptions &&
    appointmentFilterOptions.length > 1 &&
    selectedAppointmentIds &&
    onAppointmentFilterChange ? (
      <div className="indigo-timeline__filter-group">
        <AppointmentFilterDropdown
          options={appointmentFilterOptions}
          selectedIds={selectedAppointmentIds}
          onChange={onAppointmentFilterChange}
          loading={appointmentFilterLoading}
        />
      </div>
    ) : null;

  const typeFilter = (
    <div className="indigo-timeline__filter-group min-w-[150px]">
      <DropdownSelector
        options={availableTypes}
        value={filters.selectedTypes}
        onChange={(value: string[] | null) => handleTypeFilter(value || [])}
        multiple
        maxSelectedVisible={1}
        placeholder="Select entry type"
        variant="outline"
        width="full"
        allowNull
        nullLabel="All types"
      />
    </div>
  );

  const fileFilter = (
    <div className="indigo-timeline__filter-group min-w-[150px]">
      <DropdownSelector
        options={availableFiles}
        value={filters.selectedFiles}
        onChange={(value: string[] | null) => handleFileFilter(value || [])}
        multiple
        maxSelectedVisible={1}
        placeholder="Select file"
        variant="outline"
        width="full"
        allowNull
        nullLabel="All files"
      />
    </div>
  );

  const dateFilter = (
    <div className="indigo-timeline__filter-group">
      <DateRangePicker
        startDate={filters.dateRange.startDate}
        endDate={filters.dateRange.endDate}
        onChange={handleDateRangeChange}
        variant="outline"
        startPlaceholder="Start date"
        endPlaceholder="End date"
        openEnded
      />
    </div>
  );

  /** Show cites — behind the citation feature flag, and only once there is
   * actually something to show. */
  const citesFilter =
    citationsEnabled && (citationsByItemId?.size ?? 0) > 0 ? (
      <div className="indigo-timeline__filter-group indigo-timeline__checkbox-filter">
        <Checkbox
          checked={showCitations}
          onChange={(e: CheckboxChangeEvent) =>
            setShowCitations(e.target.checked)
          }
        >
          <Body size="sm" className="font-medium">
            Show cites
          </Body>
        </Checkbox>
      </div>
    ) : null;

  /** Body-part tags, two rows: "In the claim" and "Other in MR". */
  const tagRows = (
    <div className="indigo-timeline__filter-group">
      <div className="indigo-timeline__tag-row">
        <div className="indigo-timeline__tag-row-label">In the claim:</div>
        <div className="indigo-timeline__tag-filters">
          {claimedTags.map((tag) => (
            <ClickableTag
              key={tag}
              label={tag}
              isSelected={filters.searchQuery === tag}
              onClick={() =>
                handleSearchChange(filters.searchQuery === tag ? "" : tag)
              }
            />
          ))}
        </div>
      </div>

      <div className="indigo-timeline__tag-row">
        <div className="indigo-timeline__tag-row-label">Other in MR:</div>
        {showOtherTags && (
          <div className="indigo-timeline__tag-filters">
            {otherTags.map((tag) => (
              <ClickableTag
                key={tag}
                label={tag}
                isSelected={filters.searchQuery === tag}
                onClick={() =>
                  handleSearchChange(filters.searchQuery === tag ? "" : tag)
                }
              />
            ))}
          </div>
        )}
        <Button
          className="mt-[1px]"
          variant="ghost"
          size="sm"
          onClick={() => setShowOtherTags(!showOtherTags)}
        >
          {showOtherTags
            ? "Hide body parts"
            : `Show ${otherTags.length} body parts`}
        </Button>
      </div>
    </div>
  );

  // How many filters are applied behind the "Filters" button. The search
  // query is not one of them: it stays in view in the box beside the button,
  // and a body-part tag writes into that same box. The appointment scope is
  // not one either — it always has a value, and "current" is not a
  // narrowing the user needs reminding of.
  const activeFilterCount =
    (filters.selectedTypes.length > 0 ? 1 : 0) +
    (filters.selectedFiles.length > 0 ? 1 : 0) +
    (filters.dateRange.startDate !== null || filters.dateRange.endDate !== null
      ? 1
      : 0) +
    (filters.showStarredOnly ? 1 : 0) +
    (filters.showImportantOnly ? 1 : 0);

  return (
    <div className={`indigo-timeline ${className}`} data-testid={dataTestId}>
      {isNarrowViewport ? (
        <>
          {/* One line: the search box takes the width, the button reports how
              much is applied behind it. */}
          <div className="indigo-timeline__filters indigo-timeline__filters--narrow">
            {searchGroup}
            <Button
              variant="secondary"
              size="lg"
              icon={<Icon name="filter" sizeX={16} />}
              tag={
                activeFilterCount > 0 ? (
                  <Badge label={String(activeFilterCount)} size="sm" />
                ) : undefined
              }
              onClick={() => setIsFilterSheetOpen(true)}
              aria-expanded={isFilterSheetOpen}
              // Same icon override as the dash views' Filters button:
              // `Button.css` sizes every `lg` icon at 20px, which overrides
              // `sizeX` and leaves the funnel looming over its label.
              className="shrink-0 [&_svg]:!w-4 [&_svg]:!h-4"
            >
              Filters
            </Button>
          </div>

          <MobileSheet
            open={isFilterSheetOpen}
            onClose={() => setIsFilterSheetOpen(false)}
            title="Filters"
            dataTestId="Timeline-filter-sheet"
            footer={
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => setIsFilterSheetOpen(false)}
              >
                Show results
              </Button>
            }
          >
            <div className="indigo-timeline__sheet-filters">
              {appointmentFilter}
              {typeFilter}
              {fileFilter}
              {dateFilter}
              {/* The two-or-three checkboxes share a row: each is a short
                  label, and a column of them is mostly whitespace. */}
              <div className="indigo-timeline__sheet-checkboxes">
                {checkboxFilters}
                {citesFilter}
              </div>
              {tagRows}
            </div>
          </MobileSheet>
        </>
      ) : (
        /* Filter Bar. Layout is state-independent — filter controls always
           stack on top of the body-part tag rows (see Timeline.css). Only the
           extra File/Date controls reveal on expand, via their own wrapper. */
        <div className="indigo-timeline__filters">
          <div className="indigo-timeline__filters-left">{tagRows}</div>

          <div
            className={clsx(
              "indigo-timeline__filters-right",
              compactFilters && "indigo-timeline__filters-right--compact",
            )}
            ref={filtersRowRef}
          >
            {/* Checkbox filters. On a narrow card they don't fit next to the
                search + type controls, so they move in behind the filter
                toggle alongside File/Date range (see `compactFilters`). */}
            {!compactFilters && checkboxFilters}

            {citesFilter}

            {searchGroup}

            {appointmentFilter}

            {typeFilter}

            {/* Extra filters (File, Date range) — always rendered so the
                wrapper animates max-width + opacity on toggle. On a narrow
                card it also carries the checkbox filters and wraps onto its
                own line instead of stretching sideways. */}
            <div
              className={clsx(
                "indigo-timeline__extra-filters",
                !showAllFilters && "indigo-timeline__extra-filters--collapsed",
                compactFilters && "indigo-timeline__extra-filters--stacked",
              )}
              aria-hidden={!showAllFilters}
            >
              {fileFilter}

              {dateFilter}

              {/* Checkboxes come last on a narrow card: the dropdowns are
                  the wide, primary controls, and trailing checkboxes give
                  the wrapped rows a steadier left edge. */}
              {compactFilters && checkboxFilters}
            </div>

            {/* Show more/less filters toggle */}
            {(() => {
              // True when a filter hidden by the collapsed state is active —
              // surface a dot on the toggle so users don't get silently
              // filtered by a control they can't see.
              const hasHiddenActiveFilter =
                !showAllFilters &&
                (filters.selectedFiles.length > 0 ||
                  filters.dateRange.startDate !== null ||
                  filters.dateRange.endDate !== null ||
                  // On a narrow card the checkboxes hide in here too.
                  (compactFilters &&
                    (filters.showStarredOnly || filters.showImportantOnly)));
              return (
                <div
                  className={`indigo-timeline__filter-group indigo-timeline__filters-toggle ${
                    hasHiddenActiveFilter
                      ? "indigo-timeline__filters-toggle--has-hidden-active"
                      : ""
                  }`}
                >
                  <IconButton
                    iconName="filter"
                    variant="ghost"
                    size="md"
                    tooltip={
                      showAllFilters ? "Show less filters" : "Show all filters"
                    }
                    onClick={() => setShowAllFilters((prev) => !prev)}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Virtualized Timeline List */}
      {/* Wrapper must be a flex column: Safari doesn't resolve percent heights
          (react-window's inline `max-height: 100%`) against a block parent
          whose height came from flex sizing, so the List expands to full
          content height and no scrollbar appears. */}
      <div
        className="flex w-full min-h-0 flex-1 basis-0 flex-col overflow-hidden focus-visible:ring-0 focus-visible:outline-none"
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocusCapture={handleFocusIn}
      >
        {displayItems.length > 0 ? (
          <List
            // The stable gutter reserves nothing under macOS overlay
            // scrollbars, so `mr-1` is what keeps the bar off the sidebar rail
            // on those machines. It has to be margin: a scroll container paints
            // its scrollbar between the padding and border boxes, and
            // react-window's rows (`absolute; left: 0; width: 100%`) ignore
            // this element's padding anyway — their breathing room comes from
            // .indigo-timeline-row. `scrollbar-thin` is inert in Chrome/Firefox
            // (index.css sets the same standard props on `*`), kept for
            // Safari < 18.2.
            className="scrollbar-thin mr-1 py-1 [scrollbar-gutter:stable]"
            style={{ minHeight: 0 }}
            listRef={listRef}
            rowCount={displayItems.length}
            rowHeight={rowHeight}
            rowComponent={TimelineRow}
            rowProps={rowProps}
            // Don't set `rowKey`: in dynamic-height mode it remounts rows on
            // filter changes, which detaches react-window's ResizeObserver and
            // leaves stale offsets (gaps/overlaps). See #8606.
            overscanCount={3}
            onRowsRendered={handleRowsRendered}
          />
        ) : (
          <div className="indigo-timeline__empty-state">
            {emptyStateContent ?? (
              <p>No timeline items match the current filters.</p>
            )}
          </div>
        )}
      </div>

      {/* Bulk actions footer - only in edit mode */}
      {mode === "edit" && (
        <TimelineBulkActionsFooter
          items={items}
          isSyncing={isSyncing ?? false}
          lastEditedAt={lastEditedAt}
          lastPublishedAt={lastPublishedAt}
          totalDraftsCount={totalDraftsCount}
          selectedItems={selectedItems}
          onClearSelections={clearSelections}
          onBulkDiscardChanges={onBulkDiscardChanges}
          onBulkDelete={onBulkDelete}
          onCompare={onCompare}
          currentRowNumber={firstVisibleRowIndex + 1}
          totalItems={displayItems.length}
        />
      )}
    </div>
  );
};

export default Timeline;
